import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
} from "@angular/core";
import { PageEvent as PageEvent } from "@angular/material/paginator";
import { MatTableDataSource as MatTableDataSource } from "@angular/material/table";
import { Store } from "@ngrx/store";
import { stableStringify as stringify } from "src/app/helpers/stable-stringify.helper";
import { MatDialog as MatDialog } from "@angular/material/dialog";
import { BehaviorSubject, combineLatest, Subject, timer } from "rxjs";
import {
  debounce,
  distinctUntilChanged,
  filter,
  map,
  pairwise,
  startWith,
  takeUntil,
  tap,
  withLatestFrom,
} from "rxjs/operators";
import { selectAllContextParams } from "../../context/state/context.selector";
import { DataSource } from "../../data-source/state/data-source.model";
import { EaredDoveService } from "../../eared-dove/eared-dove.service";
import { ToastService } from "../toast.service";
import {
  CategoryBuilderDebounceTime,
  CategoryLookup,
  Expression,
  PaginatedHit,
  PaginatedHits,
  SentencesResults,
  SortDirection,
} from "../../category-builder/category-builder.models";
import { ExpressionService } from "../../category-builder/expression.service";
import { ExpressionExplorerComponent } from "../../category-builder/expression-explorer/expression-explorer.component";
import { selectDeltaModeState } from "../../category-builder/state/working-category.selectors";
import { ContextService } from "../../context/state/context.service";

@Component({
  selector: "app-sentence-table",
  templateUrl: "./sentence-table.component.html",
  styleUrls: ["./sentence-table.component.scss"],
  standalone: false,
})
export class SentenceTableComponent implements OnInit, OnDestroy, OnChanges {
  @Input() expression: Expression;
  @Input() rowsClickable = false;
  @Output() rowClicked: EventEmitter<PaginatedHit> = new EventEmitter();

  destroyed$ = new Subject<boolean>();

  // Behavior subjects because we need them to emit current state immediately once observed
  expression$ = new BehaviorSubject<Expression>(null);
  pageIndex$ = new BehaviorSubject<number>(0);
  @Input() pageSize = 7;
  pageIndex = 0;

  loadingSentences = true;
  sentences: PaginatedHits;
  dataSource: MatTableDataSource<ResultTableRow>;

  // Sentence count
  totalSentenceCount: number;
  sentenceHitCount = 0;
  sentenceVolumePercentage = 0;

  // Verbatim count
  totalVerbatimCount: number;
  verbatimHitCount = 0;
  verbatimVolumePercentage = 0;

  displayedColumns: string[] = ["sentence", "sentiment-value"];

  sentimentVersionId: string;

  sortSentimentBy$ = new BehaviorSubject<SortDirection>(null);
  sortSentimentBy: SortDirection = null;

  // Delta Mode
  deltaModeIsActive = false;
  deltaPlusExpression: Expression;
  deltaMinusExpression: Expression;
  // Delta Mode Hit Counts
  deltaVerbatimPlusHitCount = 0;
  deltaVerbatimMinusHitCount = 0;
  deltaSentencePlusHitCount = 0;
  deltaSentenceMinusHitCount = 0;
  // Delta Mode Percentages
  deltaVerbatimVolumePercentage = 0;
  deltaSentenceVolumePercentage = 0;
  // Displaying Delta Mode Sentences Results
  @Input() displayingDeltaPlusMinusSentences: boolean;
  @Input() displayingChipotletSentences: boolean;

  loadSentences$ = combineLatest([
    this.expression$,
    this.pageIndex$,
    this.contextService.allContext$,
    this.sortSentimentBy$,
  ]).pipe(
    map(([expression, pageIndex, context, sortBySentimentDirection]) => ({
      expression,
      pageIndex,
      dataSource: context?.ds,
      sentimentId: context?.s?.version?.uuid,
      topicId: context?.t?.version?.uuid,
      emotionId: context?.e?.version?.uuid,
      intentId: context?.i?.version?.uuid,
      categoryVersionId: context?.c?.version?.uuid,
      sortBySentimentDirection,
    })),
    filter(
      // topic/emotion/intent are retired ML dimensions and are absent in modern
      // workspaces; do not gate the sentence stream on them.
      (params) =>
        !!params.expression && !!params.dataSource && !!params.sentimentId,
    ),
    distinctUntilChanged((prev, curr) => stringify(prev) === stringify(curr)),
    startWith(null as any),
    pairwise(),
    map(([prev, curr]) => {
      // If the expression, data source, or sentiment ids changed
      // we must rest the index
      const expressionChanged =
        stringify(prev?.expression) !== stringify(curr.expression);
      const dataSourceChanged =
        prev?.dataSource?.uuid !== curr.dataSource?.uuid;
      const sentimentChanged = prev?.sentimentId !== curr.sentimentId;
      const topicChanged = prev?.topicId !== curr.topicId;
      const emotionChanged = prev?.emotionId !== curr.emotionId;
      const intentChanged = prev?.intentId !== curr.intentId;
      const sortBySentimentChanged =
        prev?.sortBySentimentDirection !== curr.sortBySentimentDirection;
      this.totalVerbatimCount =
        curr.dataSource?.verbatims || curr.dataSource?.messages;
      this.totalSentenceCount = curr.dataSource?.sentences;
      this.sortSentimentBy = curr.sortBySentimentDirection;

      if (
        expressionChanged ||
        dataSourceChanged ||
        sentimentChanged ||
        topicChanged ||
        emotionChanged ||
        intentChanged ||
        sortBySentimentChanged
      ) {
        return {
          ...curr,
          pageIndex: 0,
        };
      }
      return curr;
    }),
    tap((params) => {
      this.loadingSentences = true;
      this.pageIndex = params.pageIndex;
    }),
    debounce(() => timer(CategoryBuilderDebounceTime)),
    withLatestFrom(this.contextService.categoryLookup$),
  );

  constructor(
    private store: Store<any>,
    private earedDoveService: EaredDoveService,
    private toastService: ToastService,
    private expressionService: ExpressionService,
    private dialog: MatDialog,
    private contextService: ContextService,
  ) {}

  ngOnInit(): void {
    this.loadSentences$
      .pipe(takeUntil(this.destroyed$))
      .subscribe(
        ([
          {
            expression,
            pageIndex,
            dataSource,
            sentimentId,
            topicId,
            emotionId,
            intentId,
            categoryVersionId,
            sortBySentimentDirection,
          },
          lookup,
        ]) => {
          this.loadSentences(
            expression,
            pageIndex,
            dataSource,
            sentimentId,
            topicId,
            emotionId,
            intentId,
            lookup,
            sortBySentimentDirection,
            categoryVersionId,
          );
        },
      );

    this.store
      .select(selectDeltaModeState)
      .pipe(takeUntil(this.destroyed$))
      .subscribe(
        ({
          deltaCurrentExpression,
          deltaPlusExpression,
          deltaMinusExpression,
        }) => {
          this.deltaModeIsActive = !!deltaCurrentExpression;
          this.deltaPlusExpression = deltaPlusExpression;
          this.deltaMinusExpression = deltaMinusExpression;

          if (!this.deltaModeIsActive) this.resetDeltaModeNumbers();
        },
      );

    combineLatest([
      this.store.select(selectDeltaModeState),
      this.store.select(selectAllContextParams),
      this.contextService.categoryLookup$,
    ])
      .pipe(
        filter(([deltaModeState]) => !!deltaModeState?.deltaOriginalExpression),
        filter(([deltaModeState]) => !!deltaModeState?.deltaCurrentExpression),
        filter(([, contextParams]) => !!contextParams?.s),
        // topic/emotion/intent are retired ML dimensions and are absent in
        // modern workspaces; do not gate delta stats on them.
        filter(([, , lookup]) => !!lookup),
        takeUntil(this.destroyed$),
      )
      .subscribe(
        ([
          { deltaOriginalExpression, deltaCurrentExpression },
          contextParams,
          lookup,
        ]) => {
          const substitutedDeltaOriginal =
            this.expressionService.substituteCategoryReferences(
              deltaOriginalExpression,
              lookup,
            );

          const substitutedDeltaCurrent =
            this.expressionService.substituteCategoryReferences(
              deltaCurrentExpression,
              lookup,
            );

          this.earedDoveService
            .getDeltaStats(
              contextParams.ds,
              contextParams.s,
              contextParams.t,
              contextParams.e,
              contextParams.i,
              substitutedDeltaOriginal,
              substitutedDeltaCurrent,
            )
            .subscribe((deltaStats) => {
              this.setDeltaModeStats(deltaStats);
            });
        },
      );
  }

  ngOnChanges(): void {
    this.expression$.next(this.expression);
  }

  ngOnDestroy(): void {
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }

  onPage(event: PageEvent) {
    this.pageIndex$.next(event.pageIndex);
  }

  loadSentences(
    expression: Expression,
    pageIndex: number,
    dataSource: DataSource,
    sentimentId: string,
    topicId: string,
    emotionId: string,
    intentId: string,
    lookup: CategoryLookup,
    sortBySentimentDirection: SortDirection,
    categoryVersionId: string | null = null,
  ) {
    // Substitute category references
    const substituted = this.expressionService.substituteCategoryReferences(
      expression,
      lookup,
    );

    // Set the sentimentVersionId
    this.sentimentVersionId = sentimentId;

    // Get the sentence results
    const from = pageIndex * this.pageSize;
    const size = this.pageSize;
    this.earedDoveService
      .getCategorySentences(
        dataSource.uuid,
        sentimentId,
        topicId,
        emotionId,
        intentId,
        substituted,
        from,
        size,
        sortBySentimentDirection,
        categoryVersionId,
      )
      .subscribe(
        (sr: SentencesResults) => {
          this.sentences = sr.paginated_hits;
          this.loadingSentences = false;

          this.setSentenceAndVerbatimStats(sr);
          this.dataSource = new MatTableDataSource<ResultTableRow>(
            this.getSentenceResultsWithSentimentValue(sr.paginated_hits.hits),
          );
        },
        (errorResponse) => {
          if (errorResponse.status == 406) {
            this.toastService.openErrorToast(errorResponse.error.detail);
          } else {
            this.toastService.openErrorToast("Error loading sentence results.");
          }
          this.loadingSentences = false;
        },
      );
  }

  onRowClicked(row: PaginatedHit) {
    this.rowClicked.emit(row);
  }

  setSentenceAndVerbatimStats(sr) {
    // Set Sentence Count variables
    if (sr?.paginated_hits) {
      this.sentenceHitCount = sr.sentences;
      this.sentenceVolumePercentage =
        this.sentenceHitCount / this.totalSentenceCount;
    }

    // Set Verbatim Count variables
    this.verbatimHitCount = sr.verbatims;
    this.verbatimVolumePercentage =
      this.verbatimHitCount / this.totalVerbatimCount;
  }

  getSentenceResultsWithSentimentValue(newRows: PaginatedHit[]) {
    return newRows.map((hit) => {
      return {
        sentence: hit.source.sentence,
        sentenceId: hit.id,
        source: hit.source,
        sentiments: hit.source.sentiment.reduce(
          (dict: { [modelversionID: string]: number }, item) => {
            return {
              ...dict,
              [item.version_id]: item.predict,
            };
          },
          {},
        ),
      };
    });
  }

  setDeltaModeStats({
    sentence_plus_count,
    verbatim_plus_count,
    sentence_minus_count,
    verbatim_minus_count,
  }) {
    // Set Sentence/Verbatim Counts
    this.deltaSentencePlusHitCount = sentence_plus_count;
    this.deltaSentenceMinusHitCount = sentence_minus_count;
    this.deltaVerbatimPlusHitCount = verbatim_plus_count;
    this.deltaVerbatimMinusHitCount = verbatim_minus_count;

    // Set Sentence/Verbatim Volume Percentages
    const deltaPlusSentencePercentage =
      this.deltaSentencePlusHitCount / this.totalSentenceCount;
    const deltaMinusSentencePercentage =
      this.deltaSentenceMinusHitCount / this.totalSentenceCount;
    const deltaPlusVerbatimPercentage =
      this.deltaVerbatimPlusHitCount / this.totalVerbatimCount;
    const deltaMinusVerbatimPercentage =
      this.deltaVerbatimMinusHitCount / this.totalVerbatimCount;
    this.deltaSentenceVolumePercentage =
      deltaPlusSentencePercentage - deltaMinusSentencePercentage;
    this.deltaVerbatimVolumePercentage =
      deltaPlusVerbatimPercentage - deltaMinusVerbatimPercentage;
  }

  resetDeltaModeNumbers() {
    this.deltaVerbatimVolumePercentage = 0;
    this.deltaSentenceVolumePercentage = 0;
    this.deltaVerbatimPlusHitCount = 0;
    this.deltaVerbatimMinusHitCount = 0;
    this.deltaSentencePlusHitCount = 0;
    this.deltaSentenceMinusHitCount = 0;
  }

  searchDeltaSentences(displayDeltaPlus: boolean) {
    const expression = displayDeltaPlus
      ? this.deltaPlusExpression
      : this.deltaMinusExpression;
    this.dialog.open(ExpressionExplorerComponent, {
      data: {
        expression,
        displayDeltaPlus,
      },
      width: "60%",
    });
  }

  handleSortBySentiment() {
    if (this.sortSentimentBy === SortDirection.DESCENDING) {
      this.sortSentimentBy$.next(SortDirection.ASCENDING);
    } else if (this.sortSentimentBy === SortDirection.ASCENDING) {
      this.sortSentimentBy$.next(null);
    } else {
      this.sortSentimentBy$.next(SortDirection.DESCENDING);
    }
  }
}

interface ResultTableRow {
  sentence: string;
}
